'use client';

import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Pencil,
  Plus,
  Undo2,
  UserMinus,
  UserPlus
} from 'lucide-react';
import { useState } from 'react';

import { client, type RouterOutputs } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';

type Props = {
  postId: string;
};

type Event = RouterOutputs['posts']['activity']['events'][number];

const CardActivity = ({ postId }: Props) => {
  const queryClient = useQueryClient();
  const [undoingKey, setUndoingKey] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ['cardActivity', postId],
    queryFn: () => unwrapFromResult(client.posts.activity.query({ postId })),
    enabled: Boolean(postId),
    staleTime: 1000 * 30
  });

  const handleUndo = async (e: Event) => {
    const key = eventKey(e);
    setUndoingKey(key);
    const input =
      e.kind === 'added'
        ? { kind: 'card_added' as const, postFactiiiId: e.postFactiiiId }
        : e.kind === 'moved'
          ? { kind: 'card_moved' as const, postFactiiiId: e.postFactiiiId }
          : e.kind === 'edited'
            ? {
                kind: 'card_edited' as const,
                postEditHistoryId: e.postEditHistoryId
              }
            : e.kind === 'tagged'
              ? {
                  kind: 'card_tagged' as const,
                  postTaggedUserId: e.postTaggedUserId
                }
              : {
                  kind: 'card_untagged' as const,
                  postTaggedUserId: e.postTaggedUserId
                };
    const [error] = await client.board.undoEvent.mutate(input);
    setUndoingKey(null);
    if (error) {
      toast('Could not undo', error.message ?? '', 'error');
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['cardActivity', postId] });
  };

  if (!data || data.events.length === 0) {
    return <p className="text-sm text-textSecondary/70">No activity yet.</p>;
  }

  const latestKey = eventKey(data.events[data.events.length - 1]);

  return (
    <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
      {data.events
        .slice()
        .reverse()
        .map((e) => {
          const key = eventKey(e);
          const actor = 'by' in e ? e.by : null;
          return (
            <li key={key} className="flex items-start gap-2.5 text-sm">
              {actor ? (
                <Avatar
                  avatarKey={actor.avatarKey}
                  robohash={actor.robohash}
                  isBot={actor.isBot}
                  userId={actor.id}
                  size={24}
                />
              ) : (
                <div className="h-6 w-6 rounded-full bg-background-light" />
              )}
              <div className="min-w-0 flex-1">
                <p className="leading-snug text-textSecondary">
                  <span className="font-emphasis text-textPrimary">
                    {actor ? (actor.name ?? `@${actor.username}`) : 'Someone'}
                  </span>{' '}
                  <EventDescription event={e} />
                </p>
                <p className="mt-0.5 text-xs text-textSecondary/70">
                  {new Date(e.at).toLocaleString()}
                </p>
              </div>
              {key === latestKey && (
                <button
                  onClick={() => handleUndo(e)}
                  disabled={undoingKey === key}
                  title="Undo"
                  className="shrink-0 rounded p-1 text-textSecondary/60 transition-colors hover:bg-background-light hover:text-textPrimary disabled:opacity-40"
                >
                  <Undo2 size={14} />
                </button>
              )}
            </li>
          );
        })}
    </ol>
  );
};

const eventKey = (e: Event) =>
  e.kind === 'added' || e.kind === 'moved'
    ? `f-${e.postFactiiiId}`
    : e.kind === 'edited'
      ? `e-${e.postEditHistoryId}`
      : `t-${e.postTaggedUserId}`;

const EventDescription = ({ event }: { event: Event }) => {
  switch (event.kind) {
    case 'added':
      return (
        <>
          <Plus size={12} className="-mt-0.5 mr-0.5 inline" />
          added this card to{' '}
          <span className="font-emphasis text-textPrimary">
            {event.to.name}
          </span>
        </>
      );
    case 'moved':
      return (
        <>
          moved from{' '}
          <span className="font-emphasis text-textPrimary">
            {event.from.name}
          </span>{' '}
          <ArrowRight size={12} className="-mt-0.5 inline" />{' '}
          <span className="font-emphasis text-textPrimary">
            {event.to.name}
          </span>
        </>
      );
    case 'edited':
      return (
        <>
          <Pencil size={12} className="-mt-0.5 mr-0.5 inline" />
          edited this card
        </>
      );
    case 'tagged':
      return (
        <>
          <UserPlus size={12} className="-mt-0.5 mr-0.5 inline" />
          tagged{' '}
          <span className="font-emphasis text-textPrimary">
            {event.taggedUser.name ?? `@${event.taggedUser.username}`}
          </span>
        </>
      );
    case 'untagged':
      return (
        <>
          <UserMinus size={12} className="-mt-0.5 mr-0.5 inline" />
          untagged{' '}
          <span className="font-emphasis text-textPrimary">
            {event.taggedUser.name ?? `@${event.taggedUser.username}`}
          </span>
        </>
      );
  }
};

export default CardActivity;
